import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Home,
  Users,
  Pill,
  ClipboardList,
  AlertTriangle,
  UserCog,
  Calendar,
  Menu,
  X,
  Activity,
  Heart,
  LogOut,
  User,
  Wrench,
  Settings,
  GraduationCap,
  FileText,
  MessageSquare,
  ArrowLeftRight,
  DollarSign,
  Utensils,
  Zap,
  FileSignature,
  Shield,
  Clock,
  ChevronDown,
  ChevronRight,
  BarChart3,
  QrCode,
  Moon,
  Sun,
  Car,
  Truck,
  SunMoon,
  Bell,
  CalendarDays,
  BookOpen,
  Target,
  Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useTheme } from '@/contexts/ThemeContext';
import { communicationsApi, settingsApi, organizationsApi } from '@/services/api';
import NotificationBell from '@/components/NotificationBell';

// Module categories for filtering based on organization type
const MODULE_CATEGORIES = {
  assisted_living: ['residents', 'medications', 'dietary', 'incidents', 'dhs83', 'charting', 'care_plans'],
  clts: ['clts'],
  // Modules that should show for all organization types
  common: ['dashboard', 'communications', 'staff', 'training', 'settings', 'portal']
};

// Navigation with module mapping for permission checks
const navigation = [
  { 
    name: 'Dashboard', 
    href: '/', 
    hrefForRoles: { dsp: '/clts', driver: '/clts' }, // Role-specific redirect
    icon: Home, 
    module: 'dashboard', 
    moduleCategory: 'common' 
  },
  { name: 'Meetings', href: '/meetings', icon: Calendar, module: null, moduleCategory: 'common' },
  { name: 'Communications', href: '/communications', icon: MessageSquare, module: 'communications', moduleCategory: 'common' },
  { 
    name: 'Residents', 
    icon: Users, 
    module: 'residents',
    moduleCategory: 'assisted_living',
    isCollapsible: true,
    children: [
      { name: 'All Residents', href: '/residents', icon: Users },
      { name: 'CLTS Respite Care', href: '/residents/clts-respite', icon: Heart, requiresCLTS: true },
      { name: 'Charting', href: '/charting', icon: ClipboardList },
      { name: 'Charting Analytics', href: '/charting/analytics', icon: BarChart3 },
      { name: 'Care Plans', href: '/care-plans', icon: ClipboardList },
      { name: 'Caregiver Care Plans', href: '/caregiver-care-plans', icon: Shield },
      { name: 'Incidents', href: '/incidents', icon: AlertTriangle },
      { name: 'DHS 83 Compliance', href: '/dhs83', icon: FileSignature },
    ]
  },
  {
    name: 'CLTS',
    icon: Heart,
    module: null,
    moduleCategory: 'clts',
    roles: ['admin', 'supervisor', 'nurse', 'don', 'hr', 'driver', 'dsp'],
    isCollapsible: true,
    children: [
      { name: 'Dashboard', href: '/clts', icon: Home },
      { name: 'Participants', href: '/clts/participants', icon: Users },
      { name: 'DSP Schedule', href: '/clts/schedule', icon: Calendar },
      { name: 'DSP Workers', href: '/clts/staff', icon: UserCog },
      { name: 'Contact Compliance', href: '/clts/contact-compliance', icon: BarChart3, roles: ['admin', 'supervisor', 'nurse', 'don', 'hr'] },
      { name: 'Contact Log', href: '/clts/contacts', icon: Clock },
      { name: 'ISP Management', href: '/clts/isp', icon: Target },
      { name: 'Visit Documentation', href: '/clts/visits', icon: FileText },
      { name: 'Transportation Log', href: '/clts/transportation-log', icon: Truck },
      { name: 'Intake Forms', href: '/clts/intake', icon: ClipboardList },
      { name: 'Respite Forms', href: '/clts/respite', icon: Heart },
      { name: 'Transportation', href: '/transportation', icon: Car },
      { name: 'Billing', href: '/billing', icon: DollarSign, roles: ['admin', 'supervisor'] },
    ]
  },
  { 
    name: 'Medications', 
    icon: Pill, 
    module: 'medications',
    moduleCategory: 'assisted_living',
    isCollapsible: true,
    children: [
      { name: 'eMAR', href: '/medications', icon: Pill },
      { name: 'Treatments (TAR)', href: '/tar', icon: Activity },
    ]
  },
  { 
    name: 'Dietary', 
    icon: Utensils, 
    module: 'dietary',
    moduleCategory: 'assisted_living',
    isCollapsible: true,
    children: [
      { name: 'Dashboard', href: '/dietary', icon: Home },
      { name: 'Menu Planner', href: '/dietary/menu-planner', icon: Calendar },
      { name: 'Meal Production', href: '/dietary/production', icon: ClipboardList },
      { name: 'Tray Service', href: '/dietary/tray-service', icon: Users },
      { name: 'Scheduling', href: '/dietary/scheduling', icon: Clock },
      { name: 'Reports', href: '/dietary/reports', icon: FileText },
    ]
  },
  { 
    name: 'Business Office', 
    icon: DollarSign, 
    module: 'accounting',
    moduleCategory: 'assisted_living',
    isCollapsible: true,
    children: [
      { name: 'Resident Accounting', href: '/resident-accounting', icon: DollarSign },
      { name: 'Billing Automation', href: '/billing-automation', icon: Zap },
      { name: 'Reports', href: '/reports', icon: BarChart3 },
    ]
  },
  { 
    name: 'HR', 
    icon: Shield, 
    module: null, 
    moduleCategory: 'common',
    roles: ['admin', 'hr', 'supervisor', 'scheduler'],
    isCollapsible: true,
    children: [
      { name: 'HR Dashboard', href: '/hr', icon: Shield },
      { name: 'Applications', href: '/applications', icon: FileText },
      { name: 'Staff', href: '/staff', icon: UserCog },
      { name: 'Training', href: '/training', icon: GraduationCap },
      { name: 'Training Docs', href: '/training-docs', icon: BookOpen },
      { name: 'Payroll', href: '/payroll', icon: DollarSign },
      { name: 'Time Clock', href: '/timeclock', icon: Clock },
      { name: 'Kiosk QR Codes', href: '/timeclock/qr-codes', icon: QrCode },
      { name: 'Shifts', href: '/shifts', icon: Calendar },
      { name: 'Shift Swaps', href: '/shift-swaps', icon: ArrowLeftRight },
      { name: 'Time Off Requests', href: '/pto-requests', icon: CalendarDays },
    ]
  },
  { name: 'Maintenance Orders', href: '/service-orders', icon: Wrench, module: 'maintenance', moduleCategory: 'assisted_living' },
  { name: 'Compliance Reminders', href: '/compliance-reminders', icon: Bell, module: 'training', moduleCategory: 'assisted_living' },
  { 
    name: 'Portal', 
    icon: Users, 
    module: null,
    moduleCategory: 'common',
    roles: ['admin', 'supervisor', 'hr'],
    isCollapsible: true,
    children: [
      { name: 'Case Manager Access', href: '/portal/case-manager-invites', icon: Users },
      { name: 'Subscription', href: '/portal/subscription', icon: DollarSign },
    ]
  },
  { name: 'Settings', href: '/settings', icon: Settings, module: 'settings', moduleCategory: 'common' },
];

export function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const { canAccessModule } = usePermissions();
  const [unreadCount, setUnreadCount] = useState(0);
  const [organizationType, setOrganizationType] = useState('both');
  const [expandedSections, setExpandedSections] = useState({ HR: true, Medications: true, Dietary: true, Residents: true, 'Business Office': true, CLTS: true });

  // Fetch organization type - prefer user's organization, fallback to global settings
  useEffect(() => {
    const fetchOrgType = async () => {
      try {
        // First try to get from user's organization (multi-tenant)
        if (user?.organization_id) {
          const orgResponse = await organizationsApi.getMe();
          if (orgResponse.data?.organization_type) {
            setOrganizationType(orgResponse.data.organization_type);
            return;
          }
        }
        // Fallback to global settings for legacy users without organization
        const response = await settingsApi.getPublic();
        setOrganizationType(response.data?.organization_type || 'both');
      } catch (error) {
        // Default to both if can't fetch
        setOrganizationType('both');
      }
    };
    fetchOrgType();
  }, [user?.organization_id]);

  // Filter navigation based on organization type
  const filteredNavigation = navigation.filter(item => {
    // Case managers only see Case Manager Portal link
    if (user?.role === 'case_manager') {
      return false; // Hide all regular navigation for case managers
    }
    
    // Common modules are always shown
    if (item.moduleCategory === 'common') return true;
    
    // If organization type is 'both', show everything
    if (organizationType === 'both') return true;
    
    // Show assisted_living modules only if org type includes it
    if (item.moduleCategory === 'assisted_living') {
      return organizationType === 'assisted_living' || organizationType === 'both';
    }
    
    // Show CLTS modules only if org type includes it
    if (item.moduleCategory === 'clts') {
      return organizationType === 'clts_provider' || organizationType === 'both';
    }
    
    return true;
  }).map(item => {
    // Filter children for items that require specific modules
    if (item.children) {
      return {
        ...item,
        children: item.children.filter(child => {
          // CLTS Respite Care under Residents only shows if CLTS is enabled
          if (child.requiresCLTS) {
            return organizationType === 'clts_provider' || organizationType === 'both';
          }
          return true;
        })
      };
    }
    return item;
  });

  // Auto-expand sections when navigating to child routes
  useEffect(() => {
    const path = location.pathname;
    // Check if current path is a child of any collapsible section
    filteredNavigation.forEach(item => {
      if (item.isCollapsible && item.children) {
        const isChildActive = item.children.some(child => 
          path === child.href || (child.href !== '/' && path.startsWith(child.href))
        );
        if (isChildActive && !expandedSections[item.name]) {
          setExpandedSections(prev => ({ ...prev, [item.name]: true }));
        }
      }
    });
  }, [location.pathname, filteredNavigation]);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (canAccessModule('communications')) {
        try {
          const response = await communicationsApi.getUnreadCount();
          setUnreadCount(response.data?.unread_count || 0);
        } catch (error) {
          // Silently fail - user may not have access
        }
      }
    };
    
    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [canAccessModule]);

  // Toggle expanded section
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Filter navigation based on permissions (use filteredNavigation which already filters by org type)
  const filteredNav = filteredNavigation.filter(item => {
    // Check role-based access
    if (item.roles && !hasRole(item.roles)) {
      return false;
    }
    // Check if user has permission to view this module
    if (item.module && !canAccessModule(item.module)) {
      return false;
    }
    return true;
  });

  // Case manager gets their own simplified navigation
  const caseManagerNav = [
    { name: 'My Participants', href: '/portal/case-manager', icon: Users },
    { name: 'Profile', href: '/profile', icon: User },
  ];

  // Determine which navigation to use
  const navigationToRender = user?.role === 'case_manager' ? caseManagerNav : filteredNav;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500/20">
                <Heart className="h-5 w-5 text-emerald-400" />
              </div>
              <span className="text-xl font-bold">CareHub USA</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white hover:bg-slate-800"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {navigationToRender.map((item) => {
                // Handle collapsible sections with children
                if (item.isCollapsible && item.children) {
                  const isExpanded = expandedSections[item.name];
                  const isChildActive = item.children.some(child => 
                    location.pathname === child.href || 
                    (child.href !== '/' && location.pathname.startsWith(child.href))
                  );
                  
                  return (
                    <div key={item.name}>
                      {/* Parent item - clickable to expand/collapse */}
                      <button
                        onClick={() => toggleSection(item.name)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                          isChildActive
                            ? "bg-slate-800 text-white"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        )}
                        data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="flex-1 text-left">{item.name}</span>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      
                      {/* Children */}
                      {isExpanded && (
                        <div className="mt-1 ml-4 space-y-1 border-l border-slate-700 pl-2">
                          {item.children.map((child) => {
                            const isActive = location.pathname === child.href || 
                              (child.href !== '/' && location.pathname.startsWith(child.href));
                            
                            return (
                              <Link
                                key={child.name}
                                to={child.href}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                                  isActive
                                    ? "bg-emerald-600 text-white"
                                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                                )}
                                onClick={() => setIsOpen(false)}
                                data-testid={`nav-${child.name.toLowerCase().replace(' ', '-')}`}
                              >
                                <child.icon className="h-4 w-4" />
                                <span>{child.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Regular navigation item
                // Check for role-specific href
                const itemHref = (item.hrefForRoles && user?.role && item.hrefForRoles[user.role]) 
                  ? item.hrefForRoles[user.role] 
                  : item.href;
                const isActive = location.pathname === itemHref || 
                  (itemHref !== '/' && location.pathname.startsWith(itemHref));
                const showBadge = item.module === 'communications' && unreadCount > 0;
                
                return (
                  <Link
                    key={item.name}
                    to={itemHref}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-emerald-600 text-white"
                        : "text-slate-300 hover:bg-slate-800 hover:text-white"
                    )}
                    onClick={() => setIsOpen(false)}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="flex-1">{item.name}</span>
                    {showBadge && (
                      <Badge 
                        className="bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] text-center"
                        data-testid="unread-messages-badge"
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </Badge>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Info */}
          {user && (
            <div className="border-t border-slate-800 p-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-emerald-600 text-white text-sm">
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {user.first_name} {user.last_name}
                  </p>
                  <p className="text-xs text-slate-400 capitalize">{user.role}</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-slate-800 p-4">
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Activity className="h-4 w-4 text-emerald-500" />
              <span>System Online</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

export function Header({ onMenuClick }) {
  const { user, logout, isAuthenticated } = useAuth();
  const { themeMode, cycleTheme, setTheme, isDark, isAuto, autoSchedule } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get theme icon and tooltip based on current mode
  const getThemeIcon = () => {
    if (themeMode === 'auto') return <SunMoon className="h-5 w-5 text-purple-500" />;
    if (isDark) return <Sun className="h-5 w-5 text-yellow-500" />;
    return <Moon className="h-5 w-5 text-slate-600" />;
  };

  const getThemeTooltip = () => {
    if (themeMode === 'auto') return `Auto mode (Dark ${autoSchedule.darkStartHour}:00 - ${autoSchedule.darkEndHour}:00)`;
    if (isDark) return 'Switch to light mode';
    return 'Switch to dark mode';
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-white dark:bg-slate-900 dark:border-slate-800 px-4 lg:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuClick}
        data-testid="mobile-menu-button"
      >
        <Menu className="h-5 w-5" />
      </Button>
      
      <div className="flex-1">
        <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Care Management System</h1>
      </div>

      <div className="flex items-center gap-4">
        <div className="text-sm text-slate-500 dark:text-slate-400 hidden sm:block">
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </div>

        {/* Theme Toggle - cycles through light -> dark -> auto */}
        <Button
          variant="ghost"
          size="icon"
          onClick={cycleTheme}
          className="h-9 w-9 relative"
          data-testid="theme-toggle"
          title={getThemeTooltip()}
        >
          {getThemeIcon()}
          {isAuto && (
            <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-purple-500 border border-white dark:border-slate-900" />
          )}
        </Button>

        {isAuthenticated && (
          <NotificationBell />
        )}

        {isAuthenticated && user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 text-sm">
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline dark:text-white">{user.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>
                  <p>{user.first_name} {user.last_name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user.role}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="my-profile-link">
                <User className="h-4 w-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              {user?.role === 'admin' && (
                <DropdownMenuItem onClick={() => navigate('/organization-settings')} data-testid="org-settings-link">
                  <Building2 className="h-4 w-4 mr-2" />
                  Organization Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
                Theme
              </DropdownMenuLabel>
              <DropdownMenuItem 
                onClick={() => setTheme('light')} 
                data-testid="theme-light"
                className={themeMode === 'light' ? 'bg-slate-100 dark:bg-slate-800' : ''}
              >
                <Sun className="h-4 w-4 mr-2 text-yellow-500" />
                Light
                {themeMode === 'light' && <span className="ml-auto text-emerald-600">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setTheme('dark')} 
                data-testid="theme-dark"
                className={themeMode === 'dark' ? 'bg-slate-100 dark:bg-slate-800' : ''}
              >
                <Moon className="h-4 w-4 mr-2 text-slate-600" />
                Dark
                {themeMode === 'dark' && <span className="ml-auto text-emerald-600">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setTheme('auto')} 
                data-testid="theme-auto"
                className={themeMode === 'auto' ? 'bg-slate-100 dark:bg-slate-800' : ''}
              >
                <SunMoon className="h-4 w-4 mr-2 text-purple-500" />
                Auto (7PM - 6AM)
                {themeMode === 'auto' && <span className="ml-auto text-emerald-600">✓</span>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
